import { PriceType } from "@/common/types";
import CustomTypography from "@/components/Typography/CustomTypography";
import { Button, Form, Popover, Tag } from "antd";
import TextArea from "antd/es/input/TextArea";
import useGenerateResponseElements from "@/common/hooks/useGenerateResponseElements";
import { useMemo, useState } from "react";
import {
  FormFieldsStrategyType,
  ResponseFormElementsType,
} from "@/common/types/forms/FormFieldsStrategy.type";
import { currencyOptions } from "@/common/const";
import { useGenerateFormElements } from "@/common/hooks";
import quotesRender from "@/common/const/elementsList/quotesRender.const";
import manualResQuote from "./strategies";
import { noBidReasonOptions } from "@/common/const/filter-options";
import { useQuoteToCustomer } from "@/common/api/mutation";
import { toast } from "react-toastify";
import { queryClient } from "@/common/utils";
import { useNavigate } from "react-router";
import { usePermissions } from "@/common/contexts/Permissions.context";

const pricingSection: (FormFieldsStrategyType | null)[] = [
  {
    inputType: "select",
    label: "Currency",
    name: "currency",
    required: true,
    readonly: true,
    options: currencyOptions,
  },
  {
    inputType: "text",
    required: true,
    name: "OTE",
    label: "OTE",
    pattern: /^\d+(\.\d+)?$/,
  },
  {
    inputType: "text",
    required: true,
    name: "MRE",
    label: "MRE",
    pattern: /^\d+(\.\d+)?$/,
  },
  {
    inputType: "select",
    required: false,
    name: "quoteType",
    label: "Quote Type",
    options: [
      {
        value: "Firm",
        label: "Firm",
      },
      {
        value: "Budgetary",
        label: "Budgetary",
      },
    ],
  },
];

type ManualQuoteResponseProps = PriceType & {
  quoteType: "Budgetary" | "Firm";
  view: "customer" | "vendor";
  quoteId: number;
  customerId: number;
  vendorId: number;
  quoteLineId: number;
};
const { Item } = Form;

export default function ManualQuoteResponse(
  props: Partial<ManualQuoteResponseProps>
) {
  const {isHavePermission} = usePermissions()
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [pricingStrategy, setPricingStrategy] = useState<
    (FormFieldsStrategyType | null)[]
  >([]);
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [strategy, setStrategy] = useState<ResponseFormElementsType[]>([]);
  const type = props.type || "DIA";
  // const source = props.view;
  const { formElements: pricingElements } = useGenerateFormElements(
    pricingStrategy as FormFieldsStrategyType[]
  );
  const { mutate: submitQuoteToCustomer } = useQuoteToCustomer();
  const elements = useGenerateResponseElements(strategy);
  useMemo(() => {
    if (!props) return;
    setStrategy(
      manualResQuote[type].map((el) => {
        return {
          ...el,
          valueForLabel: props[el.attribute as keyof typeof props],
          initialValue: props[el.attribute as keyof typeof props],
        };
      })
    );
    setPricingStrategy(
      pricingSection.map((el) => {
        if (type === "fieldServices" && el?.name === "MRE") {
          return null;
        }
        return { ...el, defaultValue: props[el?.name as keyof typeof props] };
      }) as (FormFieldsStrategyType | null)[]
    );
  }, [props]);
  const changePopover = () => {
    setPopoverOpen((state) => !state);
  };
  const onFinishForm = (
    values: Partial<
      PriceType & {
        description: string | undefined;
        quoteType: "Firm" | "Budgetary";
      }
    >
  ) => {
    submitQuoteToCustomer(
      {
        quoteId: props.quoteId || 0,
        data: {
          method: "price",
          customerId: props.customerId,
          vendorId: props.vendorId,
          quoteLineId: props.quoteLineId,
          price: { ...values },
          description: values.description || "",
          quoteLineType: values.quoteType,
        },

      },
      {
        onSuccess: async () => {
          toast.success("Quote send to review");
          await queryClient.invalidateQueries();
          return navigate(-1);
        },
      }
    );
  };
  return (
    <Form form={form} onFinish={onFinishForm} disabled={!isHavePermission('quotes_vendor_edit')}>
      <div>
        <div className=" bg-white p-4  rounded-lg">
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center ">
              <CustomTypography>
                {quotesRender.find((el) => el.value === type)?.label}
              </CustomTypography>
              <Tag className="text-primary font-kanit text-[13px] leading-[13px] flex items-center justify-center border-none bg-primaryDim py-2">
                Pricing
              </Tag>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-x-4">{...pricingElements}</div>
        </div>
        <div className="flex gap-4 mt-5 items-center">
          <CustomTypography
            case={"uppercase"}
            size={"base"}
            weight={"semibold"}
          >
            Requirement
          </CustomTypography>
          <CustomTypography case={"normal"} size={"13px"} weight={"light"}>
            Please complete your proposal in Vendor Response section against
            Product Requirement
          </CustomTypography>
        </div>
        <div className="grid grid-cols-2  mt-4">
          <div className="grid grid-cols-4 ">
            <CustomTypography
              case={"uppercase"}
              color={"gray"}
              className="col-span-1 ml-1"
            >
              Attribute
            </CustomTypography>
            <CustomTypography
              className="col-span-1 -ml-1"
              case={"uppercase"}
              color={"gray"}
            >
              Required
            </CustomTypography>
            <CustomTypography
              className="col-span-2 ml-1"
              case={"uppercase"}
              color={"gray"}
            >
              Vendor's Response
            </CustomTypography>
          </div>
          <div className="grid grid-cols-4 pl-2">
            <CustomTypography
              case={"uppercase"}
              color={"gray"}
              className="col-span-1 ml-1"
            >
              Attribute
            </CustomTypography>
            <CustomTypography
              className="col-span-1 -ml-1"
              case={"uppercase"}
              color={"gray"}
            >
              Required
            </CustomTypography>
            <CustomTypography
              className="col-span-2 ml-1"
              case={"uppercase"}
              color={"gray"}
            >
              Vendor's Response
            </CustomTypography>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-5">
          {...elements}
        </div>
        <label className="flex flex-col gap-2 mb-4">
          <CustomTypography as="span" weight={"light"} className="py-0">
            Note
          </CustomTypography>
          <Item name="description">
            <TextArea
              name="description"
              placeholder="Type Some Text"
              className="bg-white border-gray-surface py-2 px-3"
            />
          </Item>
        </label>
      </div>
      <div className="flex gap-4">
        <Button
          onClick={changePopover}
          htmlType="button"
          className="font-inter text-base font-semibold h-10 bg-error hover:!bg-opacity-65 hover:!bg-error"
          type="primary"
        >
          No Bid
        </Button>
        <Button
          htmlType="submit"
          className="font-inter text-base font-semibold h-10"
          type="primary"
        >
          Submit Quote
        </Button>
      </div>
      <Popover
        open={popoverOpen}
        placement="topLeft"
        destroyTooltipOnHide
        trigger="click"
        onOpenChange={changePopover}
        content={
          <div className="flex flex-col -m-4">
            {noBidReasonOptions.map((el) => (
              <button
                onClick={() =>
                  submitQuoteToCustomer(
                    {
                      quoteId: props.quoteId || 0,
                      data: {
                        noBidReason: el.value,
                        method: "bid",
                        customerId: props.customerId,
                        vendorId: props.vendorId,
                        quoteLineId: props.quoteLineId,
                      },
                    },
                    {
                      onSuccess: async () => {
                        toast.success(
                          `Quote marked as "No Bid" with reason: ${el.label}`
                        );
                        await queryClient.invalidateQueries();
                        return navigate(-1);
                      },
                    }
                  )
                }
                className="rounded-none first:rounded-t-lg last:rounded-b-lg border last:border-none py-2 bg-white px-3"
              >
                {el.label}
              </button>
            ))}
          </div>
        }
      />
    </Form>
  );
}
